import MessageSelector from "./MessageSelector";

export default class TranslationHelper {

    constructor(driver){
        this.driver = driver;
        this.loaded = {};
        this.parsed = {};
    }

    capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    assign(obj, keyPath, value) {
        let lastKeyIndex = keyPath.length - 1;
        for (let i = 0; i < lastKeyIndex; ++i) {
            let key = keyPath[i];
            if (!(key in obj))
                obj[key] = {};
            obj = obj[key];
        }
        obj[keyPath[lastKeyIndex]] = value;
    }

    get locale() {
        return this.driver.get('app.locale', this.fallback);
    }

    get fallback() {
        return 'en';
    }

    /**
     * Get the array of locales to be checked.
     *
     * @param  locale
     * @return array
     */
    localeArray(locale) {
        let locales = [];

        if (locale) {
            locales.push(locale);
        } else {
            locales.push(this.locale);
        }

        return locales;
    }

    /**
     * Get the message selector instance.
     *
     * @return \Illuminate\Translation\MessageSelector
     */
    getSelector() {
        return new MessageSelector;
    }


    /**
     * Determine if a translation exists for a given locale.
     *
     * @param  key
     * @param  locale
     * @return bool
     */
    hasForLocale(key, locale = null) {
        return this.has(key, locale, false);
    }

    /**
     * Determine if a translation exists.
     *
     * @param  key
     * @param  locale
     * @param  fallback
     * @return bool
     */
    has(key, locale = null, fallback = true) {
        return this.get(key, [], locale, fallback) !== key;
    }

    /**
     * Get the translation for a given key.
     *
     * @param  key
     * @param  replace
     * @param  locale
     * @return string|array
     */
    trans(key, replace = [], locale = null) {
        return this.get(key, replace, locale);
    }

    /**
     * Get the translation for the given key.
     *
     * @param  key
     * @param  replace
     * @param  locale
     * @param  fallback
     * @return string|array
     */
    get(key, replace = [], locale = null, fallback = null) {

        let [namespace, group, item] = this.parseKey(key);

        // Here we will get the locale that should be used for the language line. If one
        // was not passed, we will use the default locales which was given to us when
        // the translator was instantiated. Then, we can load the lines and return.
        let locales = [];

        if (fallback) {
            locales = this.localeArray(locale);
        } else {
            if (locale) {
                locales = [locale];
            } else {
                locales = [this.locale];
            }
        }

        let line = null;

        for (let _locale of locales) {

            line = this.getLine(namespace, group, _locale, item, replace);

            if (line !== null) {
                break;
            }

        }

        // If the line doesn't exist, we will return back the key which was requested as
        // that will be quick to spot in the UI if language keys are wrong or missing
        // from the application's language files. Otherwise we can return the line.
        if (line !== null) {
            return line;
        }

        return key;
    }

    getRaw(key, replace = [], locale = null, fallback = null) {

        let [namespace, group, item] = this.parseKey(key);

        // Here we will get the locale that should be used for the language line. If one
        // was not passed, we will use the default locales which was given to us when
        // the translator was instantiated. Then, we can load the lines and return.
        let locales = [];

        if (fallback) {
            locales = this.localeArray(locale);
        } else {
            if (locale) {
                locales = [locale];
            } else {
                locales = [this.locale];
            }
        }

        let line = null;

        for (let _locale of locales) {

            line = this.getRawLine(namespace, group, _locale, item, replace);

            if (line !== null) {
                break;
            }

        }

        // If the line doesn't exist, we will return back the key which was requested as
        // that will be quick to spot in the UI if language keys are wrong or missing
        // from the application's language files. Otherwise we can return the line.
        if (line !== null) {
            return line;
        }

        return key;
    }

    /**
     * Parse a key into namespace, group, and item.
     *
     * @param  key
     * @return array
     */
    parseKey(key) {
        let segments = this.orgParseKey(key);

        if (!(segments[0])) {
            segments[0] = '*';
        }

        return segments;
    }

    /**
     * Parse a key into namespace, group, and item.
     *
     * @param  key
     * @return array
     */
    orgParseKey(key) {

        let parsed = {};

        // If we've already parsed the given key, we'll return the cached version we
        // already have, as this will save us some processing. We cache off every
        // key we parse so we can quickly return it on all subsequent requests.
        if ((this.parsed[key])) {
            return this.parsed[key];
        }

        // If the key does not contain a double colon, it means the key is not in a
        // namespace, and is just a regular configuration item. Namespaces are a
        // tool for organizing configuration items for things such as modules.
        if (key.includes("::") === false) {
            let segments = key.split(".");

            parsed = this.parseBasicSegments(segments);
        } else {
            parsed = this.parseNamespacedSegments(key);
        }

        // Once we have the parsed array of this key's elements, such as its groups
        // and namespace, we will cache each array inside a simple list that has
        // the key and the parsed array for quick look-ups for later requests.
        this.parsed[key] = parsed;
        return parsed;
    }

    /**
     * Parse an array of basic segments.
     *
     * @param  segments
     * @return array
     */
    parseBasicSegments(segments) {

        // The first segment in a basic array will always be the group, so we can go
        // ahead and grab that segment. If there is only one total segment we are
        // just pulling an entire group out of the array and not a single item.
        let group = segments.shift();

        // If there is more than one segment in this group, it means we are pulling
        // a specific item out of a group and will need to return this item name
        // as well as the group so we know which item to pull from the arrays.
        let item = segments.length > 0
            ? segments.join(".")
            : null;

        return [null, group, item];
    }

    /**
     * Parse an array of namespaced segments.
     *
     * @param  key
     * @return array
     */
    parseNamespacedSegments(key) {

        let [namespace, item] = key.split("::");

        // First we'll just explode the first segment to get the namespace and group
        // since the item should be in the remaining segments. Once we have these
        // two pieces of data we can proceed with parsing out the item's value.
        let itemSegments = item.split(".");

        let groupAndItem = this.parseBasicSegments(itemSegments.slice(1));

        return [namespace, ...groupAndItem];
    }

    /**
     * Set the parsed value of a key.
     *
     * @param  key
     * @param  parsed
     * @return void
     */
    setParsedKey(key, parsed) {
        this.parsed[key] = parsed;
    }

    /**
     * Get a translation according to an integer value.
     *
     * @param  key
     * @param  number
     * @param  replace
     * @param  locale
     * @return string
     */
    transChoice(key, number, replace = [], locale = null) {
        return this.choice(key, number, replace, locale);
    }

    /**
     * Get a translation according to an integer value.
     *
     * @param  key
     * @param  number
     * @param  replace
     * @param  locale
     * @return string
     */
    choice(key, number, replace = [], locale = null) {

        locale = this.localeForChoice(locale)

        let line = this.get(
            key, replace, locale
        );

        // If the given "number" is actually an array or countable we will simply count the
        // number of elements in an instance. This allows developers to pass an array of
        // items without having to count it on their end first which gives bad syntax.
        if (Array.isArray(number) || typeof(number) === 'string') {
            number = number.length;
        }

        replace['count'] = number;

        return this.makeReplacements(this.getSelector().choose(line, number, locale), replace);
    }

    /**
     * Get the proper locale for a choice operation.
     *
     * @param  locale
     * @return string
     */
    localeForChoice(locale) {
        if (locale) {
            return locale;
        }

        if (this.locale) {
            return this.locale;
        }

        return this.fallback;
    }

    /**
     * Retrieve a language line out the loaded array.
     *
     * @param  namespace
     * @param  group
     * @param  locale
     * @param  item
     * @param  replace
     * @return string|array|null
     */
    getLine(namespace, group, locale, item, replace) {

        this.load(namespace, group, locale);

        let loaded = this.loaded[locale][group];

        if (loaded === null) {
            return null;
        }

        if(!item) {
            return null;
        }

        let line = this.searchObject(loaded, item);

        if (typeof(line) === 'string') {
            return this.makeReplacements(line, replace);
        }

        if (Array.isArray(line) && line.length > 0) {
            return line;
        }

        return null;
    }

    getRawLine(namespace, group, locale, item, replace) {

        this.load(namespace, group, locale);

        let loaded = this.loaded[locale][group];

        if (loaded === null) {
            return null;
        }

        if(!item) {
            return null;
        }

        return this.searchObject(loaded, item);
    }

    searchObject(object, query, defaults = null) {

        if (typeof(query) === 'string') {
            query = query.split('.');
        }

        const res = query.reduce((o, i) => o ? o[i] : undefined, object);

        return (typeof res !== 'undefined') ? res : defaults;
    }

    /**
     * Make the place-holder replacements on a line.
     *
     * @param  line
     * @param  replace
     * @return string
     */

    makeReplacements(line, replace) {

        if (!replace) {
            return line;
        }

        //replace = this.sortReplacements(replace);

        for (let key in replace) {
            let value = replace[key] + "";
            key = key + "";
            line = line.split(':' + key).join(value);
            line = line.split(':' + key.toLocaleUpperCase()).join(value.toLocaleUpperCase());
            line = line.split(':' + this.capitalizeFirstLetter(key)).join(this.capitalizeFirstLetter(value));
        }

        return line;
    }

    /**
     * Sort the replacements array.
     *
     * @param  replace
     * @return array
     */
    sortReplacements(replace) {
        return [...replace].sort((a, b) => {
            if (a.length < b.length) {
                return -1;
            }
            if (a.length > b.length) {
                return 1;
            }
            return 0;
        });
    }

    /**
     * Add translation lines to the given locale.
     *
     * @param  lines
     * @param  locale
     * @param  namespace
     * @return void
     */
    addLines(lines, locale, namespace = '*') {

        for (let key in lines) {
            let value = lines[key];
            let [group, item] = key.split('.');
            this.assign(this.loaded, [namespace, group, locale, item], value);
        }

    }

    /**
     * Load the specified language group.
     *
     * @param  namespace
     * @param  group
     * @param  locale
     * @return void
     */
    load(namespace, group, locale) {
        if (this.isLoaded(namespace, group, locale)) {
            return;
        }

        // The loader is responsible for returning the array of language lines for the
        // given namespace, group, and locale. We'll set the lines in this array of
        // lines that have already been loaded so that we can easily access them.
        let lines = this.driver.get(["translations", locale, group].join("."));//$this->loader->load($locale, $group, $namespace);

        this.assign(this.loaded, [locale, group], lines);
    }

    /**
     * Determine if the given group has been loaded.
     *
     * @param  namespace
     * @param  group
     * @param  locale
     * @return bool
     */
    isLoaded(namespace, group, locale) {
        return typeof(this.loaded[namespace]) !== 'undefined'
            && typeof(this.loaded[namespace][group]) !== 'undefined'
            && typeof(this.loaded[namespace][group][locale]) !== 'undefined';
    }

}
